<template>
  <v-container fluid data-testid="defects-table">
    <v-row>
      <v-col cols="12">
        <v-row align="center" dense>
          <v-col cols="auto">
            <span class="mr-2 subtitle-1">
              {{$t('defectDefinitions')}}
            </span>
          </v-col>

          <v-col>
            <Icon
              icon="mdi-plus"
              iconColor="primary"
              :tooltipText="$t('createDefect')"
              :small="false"
              @icon-clicked="openForm()"
              :disabled="!userAssignedClaim(UserClaims.InventoryManager)"
            />
          </v-col>

          <v-col cols="auto" v-if="showDefectCsvDownload">
            <Icon
              icon="mdi-file-delimited-outline"
              :small="false"
              :tooltipText="$t('downloadCsv')"
              @icon-clicked="downloadCsvClicked"
            />
          </v-col>

          <v-col cols="auto">
            <Icon
              icon="mdi-upload"
              :small="false"
              :tooltipText="$t('importFromCsv')"
              @icon-clicked="importFromCsvClicked"
            />
          </v-col>
        </v-row>

      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <DataTable
          class="mt-n8"
          :items="defects"
          :headers="headers"
          :showSearch="false"
          :no-data-text="$t('noDefectsForLocation')"
          :customCells="customTableCells"
        >
          <template #total-tons="{item}">
            {{ numberWithCommas((item.defectLBS / NumericConstants.ShortTonDivisor), 3) }}
          </template>

          <template #category="{item}">
            {{item.category ? $t('nature') : $t('mismanufactured')}}
          </template>

          <template #default-percentage="{item}">
            {{item.defaultPercentage}}%
          </template>

          <template #status="{item}">
            <Icon
              margin="ml-3"
              dataTestId="defect-active-icon"
              :small="false"
              :icon="item.isActive ? 'mdi-checkbox-marked-circle' : 'mdi-cancel'"
              :tooltipColor="item.isActive ? 'success' : 'black'"
              :iconColor="item.isActive ? 'success' : 'black'"
              :tooltipText="item.isActive ? $t('active') : $t('notActive')"/>
          </template>

          <template #actions="{item}">
            <Icon
              icon="mdi-history"
              iconColor="primary"
              dataTestId="template-modification-history-button"
              :tooltipText="$t('history')"
              @icon-clicked="viewDefectDefinitionModifications(item)"/>

            <Icon
              icon="mdi-pencil"
              iconColor="success"
              :tooltipText="$t('edit')"
              dataTestId="defect-edit-button"
              @icon-clicked="openForm(item)"
              :disabled="!userAssignedClaim(UserClaims.InventoryManager)"/>

            <Icon
              icon="mdi-delete-forever"
              iconColor="error"
              dataTestId="defect-delete-button"
              :tooltipText="$t('delete')"
              @icon-clicked="deleteLocationDefectOnClick(item)"
              :disabled="!userAssignedClaim(UserClaims.InventoryManager)"/>
          </template>

          <template #cut="{ item }">
            <v-chip v-for="chip in deductionChipsFor(item)" :key="chip.id" small style="margin-top: 2px; margin-bottom: 2px; margin-right: 4px;">
              <Icon :icon="chip.icon" iconColor="grey darken-2"></Icon>
              <span>
                {{ chip.text }}
              </span>
            </v-chip>
          </template>
        </DataTable>
      </v-col>
    </v-row>

    <Dialog :stateId="dialogId" maxWidth="900" @close="close">
      <DefectDefinitionForm
        v-if="defectForm"
        @defect-mutated="defectMutated"
        :propDefect="focusedDefect"
        :locationId="locationId"
      />

      <ModificationHistory
        v-if="defectHistoryDialog"
        entityType="defectDefinition"
        :entityName="focusedDefect.name"
        :entityId="focusedDefect.defectDefinitionId"
      />

      <ImportDefects
        v-if="importDefectsOpen"
        :location="location"
        :defects="defects"
        @import-complete="defectMutated"
      />
    </Dialog>
  </v-container>
</template>

<script>
import { formatMoney, tonStringFromPounds } from '@/utils/NumericMutations.js'
import { defectHeaders, defectHeadersCsv } from '@/headers/Defect.js'
import { numberWithCommas } from '@/utils/NumericMutations'
import { mapActions } from 'vuex'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { UserClaims } from '@/utils/Enumerations.js'
import { userAssignedClaim } from '@/utils/ClaimUtility.js'
import { generateCsvString } from '@/utils/CSVUtility'
import saveAs from 'file-saver'
import moment from 'moment'
import { NumericConstants } from '@/utils/constants'

export default {
  name: 'MultiDefects',

  props: {
    location: {
      type: Object,
      required: true
    },
    defects: {
      type: Array,
      required: true
    }
  },

  components: {
    DefectDefinitionForm: () => import('@/components/defects/DefectDefinitionForm.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    ModificationHistory: () => import('@/components/admin/ModificationHistory.vue'),
    ImportDefects: () => import('@/components/defects/ImportDefects.vue')
  },

  data: () => ({
    NumericConstants,
    UserClaims,
    dialogId: uniqueDialogId(),
    historyDialogId: 'defect-history',
    focusedDefect: undefined,
    loading: false,
    defectForm: false,
    defectHistoryDialog: false,
    importDefectsOpen: false
  }),

  watch: {
    dialog (val) {
      if (!val) {
        this.close()
      }
    }
  },

  computed: {
    headers () {
      const headers = defectHeaders()
      const includeDeduction = this.defects.some(defect => defect.lengthDeduction || defect.diameterDeduction || defect.volumeDeduction)
      return headers.filter(h => includeDeduction || h.value !== 'cut')
    },

    locationId () {
      return this.location.locationId
    },

    customTableCells () {
      return [
        {
          slotName: 'total-tons',
          value: 'defectLBS'
        },
        {
          slotName: 'default-percentage',
          value: 'defaultPercentage'
        },
        {
          slotName: 'status',
          value: 'isActive'
        },
        {
          slotName: 'category',
          value: 'category'
        },
        {
          slotName: 'actions',
          value: 'actions'
        },
        {
          slotName: 'cut',
          value: 'cut'
        }
      ]
    },

    showDefectCsvDownload () {
      return this.defects.some(d => d.isActive)
    },

    csvHeaders () {
      return defectHeadersCsv()
    }
  },

  methods: {
    ...mapActions('defects', ['deleteLocationDefect']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    formatMoney,
    numberWithCommas,
    tonStringFromPounds,
    userAssignedClaim,

    close () {
      this.closeDialogsAtOrAbove(this.dialogId)
      this.focusedDefect = undefined
      this.defectHistoryDialog = false
      this.defectForm = false
      this.importDefectsOpen = false
    },

    async deleteLocationDefectOnClick (defect) {
      defect.locationId = this.locationId
      await this.deleteLocationDefect(defect)
      this.defectMutated()
    },

    defectMutated () {
      this.close()
      this.$emit('defect-mutated')
    },

    openForm (defect) {
      this.focusedDefect = defect
      this.defectForm = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '900' })
    },

    viewDefectDefinitionModifications (defect) {
      this.focusedDefect = defect
      this.defectHistoryDialog = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '80vw' })
    },

    importFromCsvClicked () {
      this.importDefectsOpen = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '900' })
    },

    downloadCsvClicked () {
      const csvString = generateCsvString(
        this.defects.filter(d => d.isActive),
        this.csvHeaders,
        { emptyValue: this.$t('notAvailable') })
      const blob = new Blob([csvString], { type: 'text/plain;charset=utf-8' })
      saveAs(
        blob,
        `${(this.location.name).replaceAll(/[<>:"/\\|?*.\s]+/g, '')}_${this.$t('defects')}_${moment().format('YYYYMMDD')}.csv`
      )
    },

    deductionChipsFor (defect) {
      const chips = []
      if (defect.lengthDeduction) chips.push({ id: 'lengthCut', icon: 'mdi-ruler', text: this.$t('lengthCut') })
      if (defect.diameterDeduction) chips.push({ id: 'diameterCut', icon: 'mdi-diameter', text: this.$t('diameterCut') })
      if (defect.volumeDeduction) chips.push({ id: 'volumeCut', icon: 'mdi-cube', text: this.$t('volumeCut') })
      return chips
    }
  }
}
</script>
